import React, { useEffect, useState } from "react";
import {
  ProForm,
  ProCard,
  ProFormText,
  ProFormSelect,
} from "@ant-design/pro-components";
import {
  Typography,
  Divider,
  Button,
  Row,
  Col,
  Checkbox,
  Collapse,
  Card,
  message,
  Space,
  Spin,
  Input,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  InfoCircleOutlined,
  UnlockOutlined,
  SettingOutlined,
  SafetyOutlined,
  RocketOutlined,
  QuestionCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import apiRequest from "src/common/utils/apiRequest";
import { ModulePermission } from "../types";

const { Title, Text } = Typography;

const AddEditRole = () => {
  const [permissions, setPermissions] = useState<ModulePermission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [aiContextLoading, setAiContextLoading] = useState(true);
  const [defaultActiveKey, setDefaultActiveKey] = useState<string[]>([]);

  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = ProForm.useForm();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const permissionResponse = await new apiRequest(
          "permissions/get/with_modules",
          true
        ).send();

        if (permissionResponse.status === "success") {
          setPermissions(permissionResponse.data);
        }

        if (id) {
          const roleResponse = await new apiRequest("user/role/get", true).send(
            { id }
          );

          if (roleResponse.status === "success") {
            form.setFieldsValue({
              name: roleResponse.data.name,
              status: roleResponse.data.status,
            });

            setRolePermissions(
              roleResponse.data.permissions.map((p: any) => p.permission_action)
            );

            setDefaultActiveKey(
              roleResponse.data.permissions.map((p: any) => "mi-" + p.module)
            );
          } else {
            message.error(roleResponse.message);
            navigate("/modules/setting/users/role");
          }
        }
      } catch (err) {
        message.error("Failed to load role or permission data.");
      }
      setLoading(false);
      setAiContextLoading(false);
    };

    fetchData();
  }, [id]);

  const handlePermissionChange = (checked: boolean, actionId: string) => {
    setRolePermissions((prev) =>
      checked ? [...prev, actionId] : prev.filter((id) => id !== actionId)
    );
  };

  const handleSubmit = async (values: any) => {
    if (!values.name || !rolePermissions.length) {
      message.error("Please provide a role name and select permissions.");
      return;
    }

    const payload = {
      id,
      name: values.name,
      status: values.status,
      permissions: rolePermissions,
    };

    try {
      const saveResponse = await new apiRequest(
        id ? "user/role/edit" : "user/role/add",
        true
      ).send(payload);

      if (saveResponse.status === "success") {
        message.success("Role saved successfully.");
        navigate("/modules/setting/users/role");
      } else {
        message.error(saveResponse.message || "Save failed.");
      }
    } catch (err) {
      message.error("Failed to save role.");
    }
  };

  const findModulesWithPermissionPrefix = (prefix: string): string[] => {
    const modules = permissions.filter((module) =>
      module.permissions.some((perm) =>
        perm.permission.toLowerCase().startsWith(prefix.toLowerCase())
      )
    );

    return modules.map((module) => {
      return module.id;
    });
  };

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Title level={3}>{id ? "Edit Role" : "Add New Role"}</Title>
      <Row gutter={[16, 1]}>
        <Col xs={24} lg={16}>
          <ProCard loading={loading} bordered>
            <ProForm
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              submitter={{
                render: (_, dom) => (
                  <Row justify="end">
                    <Space>
                      <Button
                        onClick={() => navigate("/modules/setting/users/role")}
                      >
                        Cancel
                      </Button>
                      {dom}
                    </Space>
                  </Row>
                ),
              }}
            >
              <Row gutter={[16, 0]} style={{ marginBottom: -15 }}>
                <Col xs={24} sm={24} lg={16}>
                  <ProFormText
                    name="name"
                    label="Role Name"
                    placeholder="Enter role name"
                    rules={[
                      { required: true, message: "Role name is required." },
                    ]}
                  />
                </Col>
                <Col xs={24} sm={24} lg={8}>
                  <ProFormSelect
                    name="status"
                    label="Status"
                    options={[
                      { label: "Active", value: 1 },
                      { label: "Inactive", value: 0 },
                    ]}
                  />
                </Col>
              </Row>
              <Divider />
              <Row gutter={[16, 0]}>
                <Col xs={24} sm={24} lg={16}>
                  <Title level={5}>Permissions</Title>
                </Col>
                <Col xs={24} sm={24} lg={8}>
                  <Input
                    name="search"
                    placeholder="Search role name"
                    onChange={(e) => {
                      if (!e.target.value) {
                        setDefaultActiveKey([]);
                        return;
                      }
                      const prefix = e.target.value.toLowerCase();
                      const founded = findModulesWithPermissionPrefix(
                        prefix
                      ).map((id) => `mi-${id}`);

                      setDefaultActiveKey(defaultActiveKey.concat(founded));
                    }}
                  />
                </Col>
              </Row>

              {loading ? (
                <Spin indicator={<LoadingOutlined spin />} />
              ) : (
                permissions.map((module, mi) => (
                  <Collapse
                    size="small"
                    key={`module-${module.id}`}
                    activeKey={defaultActiveKey}
                    onChange={(keys) =>
                      setDefaultActiveKey(Array.isArray(keys) ? keys : [keys])
                    }
                    style={{
                      marginBottom: 10,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                    bordered={false}
                    items={[
                      {
                        key: `mi-${module.id}`,
                        label: module.module.toUpperCase(),
                        children: (
                          <Card
                            size="small"
                            bordered
                            style={{ background: "#fcfcfc", marginBottom: 24 }}
                          >
                            {module.permissions.length ? (
                              module.permissions.map((perm, pi) => (
                                <div key={`${mi}-${pi}`}>
                                  <Row gutter={[8, 8]} align="middle">
                                    <Col xs={24} sm={6}>
                                      <Text>{perm.permission}</Text>
                                    </Col>
                                    <Col xs={24} sm={18}>
                                      <Checkbox.Group value={rolePermissions}>
                                        <Space wrap>
                                          {perm.actions.map((action, ai) => (
                                            <Checkbox
                                              key={action.id}
                                              value={action.id}
                                              onChange={(e) =>
                                                handlePermissionChange(
                                                  e.target.checked,
                                                  action.id
                                                )
                                              }
                                            >
                                              {action.action}
                                            </Checkbox>
                                          ))}
                                        </Space>
                                      </Checkbox.Group>
                                    </Col>
                                  </Row>
                                  {pi !== module.permissions.length - 1 && (
                                    <Divider dashed />
                                  )}
                                </div>
                              ))
                            ) : (
                              <Text type="secondary">No Permissions</Text>
                            )}
                          </Card>
                        ),
                      },
                    ]}
                  />
                ))
              )}
            </ProForm>
          </ProCard>
        </Col>

        <Col xs={0} lg={8}>
          <Card
            title={
              <Space>
                <span style={{ color: "#0050b3", fontWeight: "bold" }}>
                  🤖 Role-Based Access Guide
                </span>
                <QuestionCircleOutlined />
              </Space>
            }
            bordered
            style={{ background: "#f0f8ff", borderColor: "#91caff" }}
          >
            {aiContextLoading ? (
              <Spin indicator={<LoadingOutlined spin />} size="small" />
            ) : (
              <Space direction="vertical" size="middle">
                <Text type="secondary" style={{ fontSize: 13 }}>
                  <InfoCircleOutlined
                    style={{ color: "#1890ff", marginRight: 6 }}
                  />
                  This smart panel helps you configure secure and efficient
                  permissions for all system roles.
                </Text>

                <Text>
                  <UnlockOutlined
                    style={{ color: "#52c41a", marginRight: 8 }}
                  />
                  <b>Core Permissions:</b>
                  <ul style={{ margin: "6px 0 0 24px" }}>
                    <li>
                      <b>View:</b> Read-only access to records and modules.
                    </li>
                    <li>
                      <b>Create:</b> Add new data entries in authorized
                      sections.
                    </li>
                    <li>
                      <b>Edit:</b> Update existing data with restrictions.
                    </li>
                    <li>
                      <b>Delete:</b> Remove data when permitted.
                    </li>
                  </ul>
                </Text>

                <Text>
                  <SettingOutlined
                    style={{ color: "#faad14", marginRight: 8 }}
                  />
                  <b>Tip:</b> Align permissions with job roles to avoid
                  overexposure. Use templates for Admin, Manager, and Operator.
                </Text>

                <Text>
                  <SafetyOutlined
                    style={{ color: "#eb2f96", marginRight: 8 }}
                  />
                  <b>Security Advice:</b> Apply the principle of least
                  privilege. Only grant what’s necessary.
                </Text>

                <Text>
                  <RocketOutlined
                    style={{ color: "#722ed1", marginRight: 8 }}
                  />
                  <b>Pro Tip:</b> Automate permission auditing and use logs for
                  better traceability and compliance.
                </Text>
              </Space>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AddEditRole;
